import { Avatar, Badge, Checkbox, Flex, Stack, Text, UnstyledButton } from '@mantine/core';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon/Icon';
import { useTranslationMipManage } from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks';
import { DocumentSigningRoleType } from '@dealstack/shared/lib/generated';
import type { BulkClaimOnBehalfPreviewRoleRes } from '@dealstack/shared/lib/generated';
import type { InvestorKey } from './BulkClaimOnBehalf.types';
import { investorKey, nameInitials, roleTypeLabel } from './BulkClaimOnBehalf.utils';

interface Props {
	role: BulkClaimOnBehalfPreviewRoleRes;
	// Whether the section is open. Derived by the parent (force-open while searching).
	expanded: boolean;
	// Lower-cased, trimmed search query — signers whose name doesn't include it are hidden.
	normalizedQuery: string;
	// Investors the user has explicitly unchecked; absence means checked.
	unchecked: Set<InvestorKey>;
	onToggleExpand: (roleType: DocumentSigningRoleType) => void;
	onToggleRole: (role: BulkClaimOnBehalfPreviewRoleRes) => void;
	onToggleInvestor: (roleType: DocumentSigningRoleType, investorId: string) => void;
}

export const BulkClaimOnBehalfRoleSection = ({
	role,
	expanded,
	normalizedQuery,
	unchecked,
	onToggleExpand,
	onToggleRole,
	onToggleInvestor,
}: Props) => {
	const tMipManage = useTranslationMipManage();

	const visibleSigners = role.signers.filter(
		(signer) =>
			normalizedQuery.length === 0 || signer.name.toLowerCase().includes(normalizedQuery)
	);
	if (normalizedQuery.length > 0 && visibleSigners.length === 0) {
		return null;
	}

	const roleKeys = role.signers.map((signer) => investorKey(role.roleType, signer.investorId));
	const checkedCount = roleKeys.filter((roleKey) => !unchecked.has(roleKey)).length;
	// Investors in this role — the rows the checkboxes toggle. Not role.claimableCount, which
	// counts claimable signatures (one per document) and read as an investor count in the header.
	const total = roleKeys.length;
	const allChecked = checkedCount === total;
	const someChecked = checkedCount > 0 && !allChecked;

	const isChecked = (investorId: string) =>
		!unchecked.has(investorKey(role.roleType, investorId));

	// The badge counts documents, not investors: selected out of claimable in this role. Each
	// row contributes the document count it displays, so the badge is their sum.
	const totalDocumentCount = role.signers.reduce((sum, signer) => sum + signer.documentCount, 0);
	const checkedDocumentCount = role.signers.reduce(
		(sum, signer) => (isChecked(signer.investorId) ? sum + signer.documentCount : sum),
		0
	);

	return (
		<Stack gap={4}>
			<Flex align="center" gap="sm">
				<UnstyledButton
					onClick={() => onToggleExpand(role.roleType)}
					aria-label={tMipManage(
						expanded
							? 'documents_page.claim_on_behalf.collapse_role'
							: 'documents_page.claim_on_behalf.expand_role'
					)}
					style={{ display: 'flex', alignItems: 'center' }}
				>
					<Icon variant={expanded ? 'caret-down' : 'caret-right'} size="md" />
				</UnstyledButton>
				<Checkbox
					checked={allChecked}
					indeterminate={someChecked}
					onChange={() => onToggleRole(role)}
					label={
						<span>
							<Text span fw={600}>
								{roleTypeLabel(role.roleType, tMipManage)}
							</Text>{' '}
							({total})
						</span>
					}
				/>
				<Badge ml="auto" variant="light" radius="sm">
					{checkedDocumentCount}/{totalDocumentCount}
				</Badge>
			</Flex>

			{expanded && (
				<Stack gap="xs" pl={40}>
					{visibleSigners.map((signer) => (
						<Checkbox
							key={signer.investorId}
							checked={isChecked(signer.investorId)}
							onChange={() => onToggleInvestor(role.roleType, signer.investorId)}
							styles={{ body: { width: '100%' }, labelWrapper: { flex: 1 } }}
							label={
								<Flex align="center" gap="sm" w="100%">
									<Avatar radius="xl" size="sm" aria-hidden>
										{nameInitials(signer.name)}
									</Avatar>
									<Text>{signer.name}</Text>
									<Text c="dimmed" ml="auto">
										{tMipManage(
											'documents_page.claim_on_behalf.document_count',
											{ count: signer.documentCount }
										)}
									</Text>
								</Flex>
							}
						/>
					))}
				</Stack>
			)}
		</Stack>
	);
};
